import React, { useState, useEffect, useCallback } from "react";
import moment from "moment";
import { useSelector } from "react-redux";
import { Box, TextField } from "@mui/material";
import { authenticatedFetch } from "../../../network/api";

import NarrowDropdown2 from "../../../components/input/new/narrowDropdown2";
import IconCheckbox from "../../../components/input/new/iconCheckbox";

import "./settingsAppointmentTypes.css"; // your sidebar styles
// import "./setHours.css"; // your hours styles

// constants…
const DAYS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];
const TIMES = Array.from({ length: 13 * 4 }, (_, i) => {
  const h = Math.floor(i / 4) + 8, // Start at 8 AM
    m = (i % 4) * 15;
  const label = moment().hour(h).minute(m).format("h:mm A");
  return { label, value: label };
});
const LOCATIONS = [
  { label: "Office (Video Visit)", value: "video" },
  { label: "Office (In‑person Visit)", value: "inPerson" },
];
const RECURRENCE = [
  { label: "Every week", value: "weekly" },
  { label: "Every other week", value: "biweekly" },
  { label: "Every month", value: "monthly" },
];

// helper to parse & validate
const parseTime = (str) => {
  const [t, ampm] = str.split(" ");
  let [h, m] = t.split(":").map(Number);
  if (ampm === "PM" && h !== 12) h += 12;
  if (ampm === "AM" && h === 12) h = 0;
  return h * 60 + m;
};
const validateDaySlots = (slots) => {
  const arr = slots
    .filter((s) => s.start && s.end)
    .map((s) => ({ start: parseTime(s.start), end: parseTime(s.end) }))
    .sort((a, b) => a.start - b.start);
  for (let i = 1; i < arr.length; i++) {
    if (arr[i].start < arr[i - 1].end) return "Time slots cannot overlap";
  }
  return "";
};

export default function SettingsHours() {
  // redux
  const firmId = useSelector((s) => s.attorneyData.attorneyData?.firm_id);
  const userId = useSelector((s) => s.attorneyData.attorneyData?.user_id);

  // Sidebar state
  const [professionals, setProfessionals] = useState([]);
  const [filteredProfessionals, setFilteredProfessionals] = useState([]);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoadingPros, setIsLoadingPros] = useState(true);

  // Hours state
  const [days, setDays] = useState(
    DAYS.map((n) => ({ name: n, enabled: false, slots: [] }))
  );
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);

  // 1) Fetch firm members
  useEffect(() => {
    const fetchPros = async () => {
      try {
        const res = await fetch(
          `/api/attorney/fetchFirmMembers?firm_id=${firmId}`
        );
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setProfessionals(data.lawyers);
        setFilteredProfessionals(data.lawyers);
        const me =
          data.lawyers.find((l) => l.user_id === userId) || data.lawyers[0];
        setSelectedProfessional(me);
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingPros(false);
      }
    };
    if (firmId) fetchPros();
  }, [firmId, userId]);

  // 2) Sidebar search
  useEffect(() => {
    if (!searchTerm) return setFilteredProfessionals(professionals);
    setFilteredProfessionals(
      professionals.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [searchTerm, professionals]);

  // 3) Fetch selected provider’s schedule
  const fetchSchedule = useCallback(async () => {
    if (!selectedProfessional) return;
    try {
      const res = await authenticatedFetch(
        `/api/attorney/webapp/calendar/recurringSchedules?user_id=${selectedProfessional.user_id}`
      );
      const { schedules } = await res.json();
      // init blank days
      const byDay = DAYS.map((n) => ({ name: n, enabled: false, slots: [] }));
      schedules.forEach((s) => {
        const di = s.day_of_week;
        // parse recurrence
        let rec = "weekly";
        const m = s.rrule.match(/INTERVAL=(\d+)/);
        if (s.rrule.startsWith("FREQ=MONTHLY")) rec = "monthly";
        else if (m && +m[1] === 2) rec = "biweekly";
        // add slot
        byDay[di].enabled = true;
        byDay[di].slots.push({
          start: moment.utc(s.start_time, "HH:mm:ss").local().format("h:mm A"),
          end: moment.utc(s.end_time, "HH:mm:ss").local().format("h:mm A"),
          locations: s.location_types || [],
          recurrence: rec,
        });
      });
      setDays(byDay);
      setErrors({});
      setDirty(false);
    } catch (err) {
      console.error("load schedule", err);
    }
  }, [selectedProfessional]);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  // markDirty helper
  const markDirty = () => {
    if (!dirty) setDirty(true);
  };

  // slots handlers (same as SetHours)
  const toggleDay = (di) => {
    markDirty();
    setDays((ds) =>
      ds.map((d, i) => {
        if (i !== di) return d;
        const will = !d.enabled;
        const slots =
          will && d.slots.length === 0
            ? [{ start: "", end: "", locations: [], recurrence: "weekly" }]
            : d.slots;
        return { ...d, enabled: will, slots };
      })
    );
    setErrors((e) => ({ ...e, [di]: "" }));
  };
  const updateSlot = (di, si, f, v) => {
    markDirty();
    const ns = days[di].slots.map((s, i) => (i === si ? { ...s, [f]: v } : s));
    setErrors((e) => ({ ...e, [di]: validateDaySlots(ns) }));
    setDays((ds) => ds.map((d, i) => (i === di ? { ...d, slots: ns } : d)));
  };
  const addSlot = (di) => {
    markDirty();
    const ns = [
      ...days[di].slots,
      { start: "", end: "", locations: [], recurrence: "weekly" },
    ];
    setErrors((e) => ({ ...e, [di]: validateDaySlots(ns) }));
    setDays((ds) =>
      ds.map((d, i) => (i === di ? { ...d, slots: ns, enabled: true } : d))
    );
  };
  const deleteSlot = (di, si) => {
    markDirty();
    const ns = days[di].slots.filter((_, i) => i !== si);
    setErrors((e) => ({ ...e, [di]: "" }));
    setDays((ds) =>
      ds.map((d, i) =>
        i === di ? { ...d, slots: ns, enabled: ns.length > 0 } : d
      )
    );
  };

  // 4) Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    // simple validation
    const slotErrs = {};
    days.forEach((d, di) => {
      if (d.enabled) {
        if (d.slots.some((s) => !s.start || !s.end))
          slotErrs[di] = "Fill in both start/end for all slots.";
        const ov = validateDaySlots(d.slots);
        if (ov) slotErrs[di] = ov;
      }
    });
    if (Object.keys(slotErrs).length) {
      setErrors(slotErrs);
      return;
    }
    // build payload
    const slotsPayload = days.flatMap((d, di) =>
      d.enabled
        ? d.slots.map((s) => {
            const startUtc = moment(s.start, "h:mm A").utc().format("HH:mm:ss");
            const endUtc = moment(s.end, "h:mm A").utc().format("HH:mm:ss");
            return {
              day_of_week: di,
              start_time: startUtc,
              end_time: endUtc,
              recurrence_type: s.recurrence,
              interval: s.recurrence === "biweekly" ? 2 : 1,
              start_date: null,
              end_date: null,
              location_types: s.locations,
            };
          })
        : []
    );
    try {
      const res = await authenticatedFetch(
        `/api/attorney/webapp/calendar/recurringSchedules?user_id=${selectedProfessional.user_id}`,
        {
          method: "POST",
          body: JSON.stringify({ slots: slotsPayload, initial: false }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      alert("Saved!");
      fetchSchedule();
    } catch (err) {
      console.error(err);
      alert("Save failed: " + err.message);
    }
  };

  return (
    <div className="appt-settingsContainer">
      {/* SIDEBAR */}
      <Box className="appt-settingsSidebar">
        <TextField
          placeholder="Find attorney..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
        />
        <Box component="ul" className="appt-providerList">
          {isLoadingPros ? (
            <li>Loading…</li>
          ) : (
            filteredProfessionals.map((p) => (
              <li
                key={p.user_id}
                className={
                  "appt-providerItem" +
                  (selectedProfessional?.user_id === p.user_id
                    ? " selected"
                    : "")
                }
                onClick={() => setSelectedProfessional(p)}
              >
                {p.name}
              </li>
            ))
          )}
        </Box>
      </Box>

      {/* HOURS FORM */}
      <form
        className="smh-container"
        style={{ marginTop: "0px", padding: "0px" }}
        onSubmit={handleSubmit}
      >
        <h1 className="smh-title">Set Up Your Client Meeting Hours</h1>
        <p className="smh-subtitle">
          Review or adjust your available hours for client meetings for{" "}
          <b>{selectedProfessional?.name}</b>.
        </p>

        {days.map((day, di) => (
          <div key={day.name} className="smh-day-row">
            <IconCheckbox
              className="smh-day-checkbox"
              checked={day.enabled}
              onChange={() => toggleDay(di)}
              label={day.name}
            />
            <div
              className={`smh-slots-container ${
                !day.enabled ? "smh-disabled" : ""
              }`}
            >
              {day.slots.map((slot, si) => (
                <div key={si} className="smh-slot-row">
                  <NarrowDropdown2
                    placeholder="Start"
                    items={TIMES}
                    value={slot.start}
                    onChange={(v) => updateSlot(di, si, "start", v)}
                  />
                  <span className="smh-dash">–</span>
                  <NarrowDropdown2
                    placeholder="End"
                    items={TIMES}
                    value={slot.end}
                    onChange={(v) => updateSlot(di, si, "end", v)}
                  />
                  <NarrowDropdown2
                    placeholder="Select location"
                    items={LOCATIONS}
                    multiple
                    value={slot.locations}
                    onChange={(v) => updateSlot(di, si, "locations", v)}
                  />
                  <NarrowDropdown2
                    placeholder="Frequency"
                    items={RECURRENCE}
                    value={slot.recurrence}
                    onChange={(v) => updateSlot(di, si, "recurrence", v)}
                  />
                  <button
                    type="button"
                    className="smh-delete-slot"
                    onClick={() => deleteSlot(di, si)}
                  >
                    Delete
                  </button>
                </div>
              ))}
              {day.enabled && (
                <button
                  type="button"
                  className="smh-add-slot"
                  onClick={() => addSlot(di)}
                >
                  Add another
                </button>
              )}
              {errors[di] && <div className="smh-error-msg">{errors[di]}</div>}
            </div>
          </div>
        ))}

        <div className="smh-footer">
          <p className="help-text">Need help?</p>
          <button type="submit" className="appt-confirm-btn" disabled={!dirty}>
            Submit
          </button>
        </div>
      </form>
    </div>
  );
}
